import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ArrowRight, CheckCircle, Download, Calendar, Clock, Users, Target, ArrowLeft, Mail, Building2, Phone } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import tulioIcon from './assets/tulio.png'
import Navigation from './Navigation';
const CaseStudyDetailPage = () => {
  const { caseId } = useParams();
  const [showDownloadForm, setShowDownloadForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    jobTitle: '',
    phone: '',
    interests: ''
  });

  const caseStudyDetails = {
    'bigfoot-logistics': {
      title: "Bigfoot Logistics",
      subtitle: "Fleet Optimization & Predictive Maintenance System",
      industry: "Logistics",
      client: "Bigfoot Logistics Pte Ltd",
      location: "Singapore",
      overview: "Bigfoot Logistics, a leading delivery and logistics company in Southeast Asia, was facing significant operational challenges with their fleet management. Frequent vehicle breakdowns, unplanned maintenance, and inefficient route scheduling were impacting customer satisfaction and increasing operational costs.",
      challenge: {
        description: "The company operated a fleet of 500+ vehicles across Singapore and Malaysia, but lacked real-time visibility into vehicle health and performance. Manual scheduling processes led to suboptimal route planning, while reactive maintenance approaches resulted in costly downtime and missed deliveries.",
        painPoints: [
          "40% of vehicle downtime was unplanned, affecting delivery schedules",
          "Manual route optimization taking 2-3 hours daily per dispatcher", 
          "Maintenance costs 25% higher than industry average due to reactive approach",
          "Customer complaints increased by 15% due to delivery delays",
          "No real-time tracking of vehicle performance metrics"
        ]
      },
      solution: {
        description: "Tulio Labs developed a comprehensive IoT-enabled fleet management platform with predictive maintenance capabilities, real-time route optimization, and advanced analytics dashboard for operations management.",
        components: [
          "IoT sensors installed across entire fleet for real-time monitoring",
          "Machine learning models for predictive maintenance scheduling",
          "Real-time route optimization engine with traffic integration",
          "Mobile applications for drivers and field technicians",
          "Executive dashboard with KPI tracking and reporting",
          "Integration with existing ERP and customer management systems"
        ],
        technologies: ["IoT Sensors", "Machine Learning", "Real-time Analytics", "Mobile Development", "Cloud Infrastructure", "API Integration"]
      },
      implementation: {
        timeline: "16 weeks",
        teamSize: "8 engineers",
        phases: [
          { phase: "Discovery & Planning", duration: "3 weeks", description: "Fleet analysis, sensor selection, architecture design" },
          { phase: "IoT Deployment", duration: "4 weeks", description: "Sensor installation, connectivity setup, data pipeline creation" },
          { phase: "AI Model Development", duration: "5 weeks", description: "ML model training, predictive algorithms, optimization engine" },
          { phase: "Application Development", duration: "3 weeks", description: "Dashboard creation, mobile apps, system integration" },
          { phase: "Testing & Deployment", duration: "1 week", description: "UAT, performance testing, go-live support" }
        ]
      },
      results: {
        quantitative: [
          { metric: "Vehicle Downtime", before: "15%", after: "9%", improvement: "40% reduction" },
          { metric: "Maintenance Costs", before: "$2.5M annually", after: "$1.9M annually", improvement: "25% decrease" },
          { metric: "Route Planning Time", before: "2-3 hours", after: "15 minutes", improvement: "90% reduction" },
          { metric: "Customer Satisfaction", before: "82%", after: "95%", improvement: "13% increase" },
          { metric: "Fuel Efficiency", before: "12 km/L", after: "15 km/L", improvement: "25% improvement" }
        ],
        qualitative: [
          "Real-time visibility into entire fleet operations",
          "Proactive maintenance scheduling preventing breakdowns",
          "Automated route optimization reducing manual workload",
          "Improved driver satisfaction with modern mobile tools",
          "Enhanced customer experience with accurate delivery tracking"
        ]
      },
      testimonial: {
        quote: "Tulio Labs transformed our entire fleet operations. The predictive maintenance alone saved us hundreds of thousands in unplanned repairs, and our customers are happier than ever with reliable deliveries.",
        author: "Operations Director",
        company: "Bigfoot Logistics",
        image: "testimonial-bigfoot.jpg"
      }
    },
    'cristofori-music': {
      title: "Cristofori Music",
      subtitle: "Retail & ERP Modernization Platform",
      industry: "Retail",
      client: "Cristofori Music School Pte Ltd",
      location: "Singapore, Malaysia",
      overview: "Cristofori Music, Singapore's largest music education provider with 50+ outlets, needed to modernize their fragmented legacy systems to improve operational efficiency and customer experience across their retail and educational operations.",
      challenge: {
        description: "The company was running multiple disconnected systems for different functions, making it difficult to get real-time insights into business performance and causing significant operational inefficiencies.",
        painPoints: [
          "5 different legacy systems with no data integration",
          "Manual inventory management leading to 20% stock-outs",
          "Inconsistent customer experience across locations",
          "Financial reporting taking 2-3 weeks to consolidate",
          "No real-time visibility into sales performance across outlets"
        ]
      },
      solution: {
        description: "Developed a comprehensive ERP solution integrating all business functions with real-time analytics, automated inventory management, and unified customer experience platform.",
        components: [
          "Unified ERP system with modules for inventory, sales, HR, and finance",
          "Point-of-sale integration across all 50+ locations",
          "Real-time inventory management with automated reordering",
          "Customer relationship management with loyalty program",
          "Executive dashboard with real-time business intelligence",
          "Mobile applications for staff and management"
        ],
        technologies: ["ERP Integration", "POS Systems", "Real-time Analytics", "CRM", "Cloud Infrastructure", "Mobile Development"]
      },
      implementation: {
        timeline: "20 weeks",
        teamSize: "12 engineers",
        phases: [
          { phase: "System Analysis", duration: "3 weeks", description: "Legacy system audit, data mapping, integration planning" },
          { phase: "Core ERP Development", duration: "8 weeks", description: "ERP modules development, database design, API creation" },
          { phase: "POS Integration", duration: "4 weeks", description: "POS system integration, payment processing, real-time sync" },
          { phase: "Analytics & Reporting", duration: "3 weeks", description: "Dashboard development, reporting engine, mobile apps" },
          { phase: "Migration & Training", duration: "2 weeks", description: "Data migration, staff training, go-live support" }
        ]
      },
      results: {
        quantitative: [
          { metric: "Stock-outs", before: "20%", after: "4%", improvement: "80% reduction" },
          { metric: "Reporting Time", before: "2-3 weeks", after: "Real-time", improvement: "100% faster" },
          { metric: "Inventory Turnover", before: "4x annually", after: "6x annually", improvement: "50% increase" },
          { metric: "Customer Satisfaction", before: "78%", after: "92%", improvement: "18% increase" },
          { metric: "Operational Efficiency", before: "Baseline", after: "+35%", improvement: "35% improvement" }
        ],
        qualitative: [
          "Unified customer experience across all locations",
          "Real-time business insights for better decision making",
          "Automated inventory management reducing manual work",
          "Improved staff productivity with modern tools",
          "Enhanced customer loyalty through integrated programs"
        ]
      },
      testimonial: {
        quote: "The integrated ERP system gave us the visibility we never had before. We can now make data-driven decisions and our customers enjoy a consistent experience across all our outlets.",
        author: "CTO",
        company: "Cristofori Music",
        image: "testimonial-cristofori.jpg"
      }
    },
    
  'artc-industrial': {
    title: "ARTC (A*STAR)",
    subtitle: "Industrial Monitoring & Anomaly Detection",
    industry: "Industrial",
    client: "Advanced Remanufacturing and Technology Centre (A*STAR)",
    location: "Singapore",
    overview:
      "ARTC required a solution to monitor multiple industrial machines, collect real-time sensor data, and detect anomalies to prevent costly breakdowns and improve manufacturing efficiency.",
    challenge: {
      description:
        "Traditional monitoring relied on periodic manual inspections, which failed to detect early warning signs of machine failure. Data from various sensors lacked centralization, making predictive maintenance impossible.",
      painPoints: [
        "Frequent unplanned machine downtime",
        "No centralized data platform for sensor streams",
        "Manual inspections delaying fault detection",
        "Inability to visualize machine health in real-time",
        "Reactive maintenance increasing production costs",
      ],
    },
    solution: {
      description:
        "Built a full IoT + Cloud monitoring platform integrating edge devices, streaming pipelines, ML-driven anomaly detection, and visual dashboards for proactive maintenance.",
      components: [
        "Edge devices installed for continuous sensor data collection",
        "Data streaming pipeline using real-time processing",
        "Machine learning model for anomaly detection and failure prediction",
        "Operator dashboards for live visualization and alerts",
        "Integration with existing maintenance systems",
      ],
      technologies: [
        "IoT Edge Computing",
        "Stream Processing",
        "Anomaly Detection",
        "Cloud Infrastructure",
        "Real-time Dashboards",
      ],
    },
    implementation: {
      timeline: "14 weeks",
      teamSize: "6 engineers",
      phases: [
        { phase: "Requirements & Design", duration: "2 weeks", description: "Data flow design, edge architecture planning" },
        { phase: "IoT Setup", duration: "4 weeks", description: "Edge device configuration and data collection" },
        { phase: "Pipeline & ML Development", duration: "5 weeks", description: "Stream processing, anomaly detection, and alerting system" },
        { phase: "Dashboard & Integration", duration: "2 weeks", description: "Frontend visualization and API integration" },
        { phase: "Testing & Rollout", duration: "1 week", description: "Performance testing, calibration, and go-live" },
      ],
    },
    results: {
      quantitative: [
        { metric: "Equipment Downtime", before: "Baseline", after: "-60%", improvement: "60% reduction" },
        { metric: "Fault Detection Speed", before: "Manual", after: "Real-time", improvement: "Instant alerts" },
        { metric: "Maintenance Costs", before: "High", after: "Reduced", improvement: "Significant savings" },
      ],
      qualitative: [
        "Early fault detection using ML models",
        "Real-time monitoring of industrial assets",
        "Preventive maintenance enabled by anomaly alerts",
        "Improved production reliability and uptime",
      ],
    },
    testimonial: {
      quote:
        "The IoT monitoring system gave us real-time insights we never had before. We can now anticipate failures and schedule maintenance before issues arise.",
      author: "Head of Operations",
      company: "ARTC (A*STAR)",
      image: "testimonial-artc.jpg",
    },
  },

  'fintech-ai-platform': {
    title: "FinTech AI Assistant",
    subtitle: "AI-driven Customer Service Automation",
    industry: "Finance",
    client: "Confidential FinTech Client",
    location: "Singapore",
    overview:
      "The client faced an overwhelming number of customer inquiries, resulting in long response times and reduced satisfaction. Manual processes couldn't keep up with rapid growth in users.",
    challenge: {
      description:
        "Customer support agents were handling repetitive queries manually. The company lacked automation, leading to increased response times, high operational costs, and inconsistent service quality.",
      painPoints: [
        "High volume of repetitive support queries",
        "Slow response times impacting customer retention",
        "High costs due to large support team requirements",
        "No AI or automation in existing workflow",
      ],
    },
    solution: {
      description:
        "Built an AI-powered customer service assistant leveraging NLP to understand customer intent, automate responses, and escalate complex cases efficiently.",
      components: [
        "Natural language processing (NLP) for understanding queries",
        "AI chatbot integrated with ticketing systems",
        "Automated ticket routing and prioritization",
        "Analytics dashboard for tracking performance",
        "Integration with CRM and existing support tools",
      ],
      technologies: [
        "Natural Language Processing",
        "AI Chatbots",
        "Machine Learning",
        "API Integration",
        "Analytics Dashboard",
      ],
    },
    implementation: {
      timeline: "12 weeks",
      teamSize: "5 engineers",
      phases: [
        { phase: "Discovery & Design", duration: "2 weeks", description: "Workflow mapping and conversation design" },
        { phase: "Model Training", duration: "4 weeks", description: "NLP intent recognition and dataset preparation" },
        { phase: "Bot Development", duration: "3 weeks", description: "Chatbot and automation integration" },
        { phase: "Testing & Analytics Setup", duration: "2 weeks", description: "Performance testing and dashboard configuration" },
        { phase: "Deployment", duration: "1 week", description: "Rollout and live monitoring" },
      ],
    },
    results: {
      quantitative: [
        { metric: "Response Time", before: "30 min avg", after: "6 min avg", improvement: "80% faster" },
        { metric: "Query Automation", before: "0%", after: "70%", improvement: "70% automated" },
        { metric: "Customer Satisfaction", before: "75%", after: "90%", improvement: "15% increase" },
      ],
      qualitative: [
        "Drastic improvement in response time",
        "Increased customer satisfaction with faster resolutions",
        "Reduced workload for human agents",
        "Scalable AI-driven support infrastructure",
      ],
    },
    testimonial: {
      quote:
        "Our AI assistant revolutionized support efficiency. Over 70% of customer queries are now handled automatically, freeing agents for complex tasks.",
      author: "Customer Support Director",
      company: "Confidential FinTech Company",
      image: "testimonial-fintech.jpg",
    },
  },

  'healthcare-data-platform': {
    title: "Healthcare Analytics Platform",
    subtitle: "Patient Data Integration & Predictive Insights",
    industry: "Healthcare",
    client: "Confidential Healthcare Provider",
    location: "Malaysia",
    overview:
      "The healthcare provider struggled with fragmented patient data across multiple systems, limiting care coordination and making outcome analysis difficult.",
    challenge: {
      description:
        "Disparate systems prevented doctors from accessing full patient histories. Manual data entry and outdated reporting delayed clinical decisions.",
      painPoints: [
        "Patient data scattered across multiple systems",
        "No real-time analytics for monitoring or diagnosis",
        "Compliance issues with data privacy and HIPAA standards",
        "Slow manual processes reducing efficiency",
      ],
    },
    solution: {
      description:
        "Developed a secure, cloud-based healthcare data platform integrating patient records from multiple sources with predictive analytics for outcome improvement.",
      components: [
        "Data integration engine for EHR systems",
        "Real-time dashboards for clinicians",
        "Predictive analytics for diagnosis accuracy",
        "HIPAA-compliant secure cloud infrastructure",
        "Role-based access control for sensitive information",
      ],
      technologies: [
        "Healthcare Data Integration",
        "HIPAA Compliance",
        "Predictive Analytics",
        "Real-time Monitoring",
        "Secure Cloud Infrastructure",
      ],
    },
    implementation: {
      timeline: "18 weeks",
      teamSize: "10 engineers",
      phases: [
        { phase: "Data Mapping & Design", duration: "4 weeks", description: "EHR audit, schema mapping, compliance framework" },
        { phase: "Integration Development", duration: "6 weeks", description: "APIs and connectors for hospital systems" },
        { phase: "Analytics & Prediction", duration: "4 weeks", description: "Predictive model training and validation" },
        { phase: "Security Implementation", duration: "3 weeks", description: "HIPAA and role-based access setup" },
        { phase: "Testing & Deployment", duration: "1 week", description: "End-to-end testing and rollout" },
      ],
    },
    results: {
      quantitative: [
        { metric: "Diagnosis Accuracy", before: "Baseline", after: "+30%", improvement: "30% improvement" },
        { metric: "Data Access Speed", before: "Manual", after: "Instant", improvement: "Real-time access" },
        { metric: "Regulatory Compliance", before: "Partial", after: "Full", improvement: "100% compliance" },
      ],
      qualitative: [
        "Unified patient records improving care quality",
        "Predictive analytics aiding faster diagnoses",
        "Real-time health monitoring and alerts",
        "Full compliance with healthcare data standards",
      ],
    },
    testimonial: {
      quote:
        "With our new analytics platform, doctors have real-time access to comprehensive patient insights. It’s a game changer for patient care quality.",
      author: "Chief Medical Officer",
      company: "Confidential Healthcare Provider",
      image: "testimonial-healthcare.jpg",
    },
  },

  'ecommerce-optimization': {
    title: "E-commerce Platform Scaling",
    subtitle: "High-Performance Marketplace Optimization",
    industry: "E-commerce",
    client: "Confidential E-commerce Brand",
    location: "Singapore",
    overview:
      "A rapidly growing e-commerce marketplace faced system slowdowns and crashes during traffic spikes, impacting user experience and sales conversions.",
    challenge: {
      description:
        "The existing monolithic platform struggled with scalability. Page loads were slow, databases frequently overloaded, and the system lacked proper caching and traffic distribution mechanisms.",
      painPoints: [
        "Performance bottlenecks during high traffic",
        "Frequent downtime during sale events",
        "Inefficient database queries slowing response time",
        "No automated scaling or caching strategy",
      ],
    },
    solution: {
      description:
        "Redesigned platform architecture using microservices and cloud-native infrastructure, implementing caching, database optimization, and CDN for global performance improvement.",
      components: [
        "Microservices-based backend architecture",
        "Auto-scaling and load balancing setup",
        "CDN for faster global content delivery",
        "Database query optimization and caching",
        "Monitoring dashboard for performance metrics",
      ],
      technologies: [
        "Microservices Architecture",
        "Auto-scaling",
        "Database Optimization",
        "CDN Implementation",
        "Performance Monitoring",
      ],
    },
    implementation: {
      timeline: "22 weeks",
      teamSize: "15 engineers",
      phases: [
        { phase: "Architecture Design", duration: "4 weeks", description: "Infrastructure planning and service breakdown" },
        { phase: "Microservices Migration", duration: "8 weeks", description: "Service decoupling and deployment" },
        { phase: "Optimization Layer", duration: "5 weeks", description: "Caching, database tuning, CDN setup" },
        { phase: "Testing & Scalability Validation", duration: "3 weeks", description: "Load testing and performance tuning" },
        { phase: "Deployment & Monitoring", duration: "2 weeks", description: "Go-live and continuous monitoring" },
      ],
    },
    results: {
      quantitative: [
        { metric: "Uptime", before: "95%", after: "99.9%", improvement: "99.9% uptime achieved" },
        { metric: "Page Load Speed", before: "5s", after: "1.5s", improvement: "70% faster" },
        { metric: "Traffic Capacity", before: "1x", after: "5x", improvement: "5x increase" },
        { metric: "Conversion Rate", before: "Baseline", after: "+25%", improvement: "25% increase" },
      ],
      qualitative: [
        "Seamless scalability during peak sale events",
        "Lightning-fast user experience globally",
        "Stable infrastructure with automated scaling",
        "Significant improvement in conversion and revenue",
      ],
    },
    testimonial: {
      quote:
        "Our platform now handles 5x more traffic effortlessly. The new architecture gave us both speed and stability, resulting in record-breaking sales.",
      author: "CTO",
      company: "Confidential E-commerce Company",
      image: "testimonial-ecommerce.jpg",
    },
  },
};


  const caseStudy = caseStudyDetails[caseId];
  if (!caseStudy) {
    return <div>Case study not found</div>;
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real app, this would submit to your backend
    alert('Thank you! Your download link has been sent to your email address.');
    setShowDownloadForm(false);
    setFormData({
      name: '',
      email: '',
      company: '',
      jobTitle: '',
      phone: '',
      interests: ''
    });
  };

  return (
    <div className="min-h-screen bg-white">
       {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
             <Link to="/" className="flex items-center space-x-3">
            <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            
            <Navigation />
          </div>
        </div>
      </header>

      {/* Case Study Hero */}
      <section className="py-16 px-4 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          <Link to="/case-studies" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Case Studies
          </Link>
          
          <div className="mb-8">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700">{caseStudy.industry}</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">{caseStudy.title}</h1>
            <p className="text-xl text-slate-600 mb-8">{caseStudy.subtitle}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center space-x-3">
                <Building2 className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="text-sm text-slate-500">Client</p>
                  <p className="font-semibold">{caseStudy.client}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="text-sm text-slate-500">Timeline</p>
                  <p className="font-semibold">{caseStudy.implementation.timeline}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Users className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="text-sm text-slate-500">Team Size</p>
                  <p className="font-semibold">{caseStudy.implementation.teamSize}</p>
                </div>
              </div>
            </div>

            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700 mr-4"
              onClick={() => setShowDownloadForm(true)}
            >
              <Download className="w-5 h-5 mr-2" />
              Download Full Case Study
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Discuss Similar Project
            </Button>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Overview</h2>
          <p className="text-lg text-slate-600 leading-relaxed">{caseStudy.overview}</p>
        </div>
      </section>

      {/* Challenge */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">The Challenge</h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">{caseStudy.challenge.description}</p>
          
          <div className="bg-white rounded-xl p-8 shadow-sm">
            <h3 className="text-xl font-bold mb-6">Key Pain Points</h3>
            <ul className="space-y-3">
              {caseStudy.challenge.painPoints.map((point, index) => (
                <li key={index} className="flex items-start">
                  <Target className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Our Solution</h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">{caseStudy.solution.description}</p>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-6">Solution Components</h3>
              <ul className="space-y-3">
                {caseStudy.solution.components.map((component, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{component}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-6">Technologies Used</h3>
              <div className="grid grid-cols-2 gap-3">
                {caseStudy.solution.technologies.map((tech, index) => (
                  <div key={index} className="bg-slate-100 rounded-lg p-3 text-center">
                    <span className="text-slate-700 font-medium text-sm">{tech}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Timeline */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center">Implementation Timeline</h2>
          <div className="space-y-8">
            {caseStudy.implementation.phases.map((phase, index) => (
              <div key={index} className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {index + 1}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-xl font-bold">{phase.phase}</h3>
                      <Badge variant="secondary">{phase.duration}</Badge>
                    </div>
                    <p className="text-slate-600">{phase.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center">Results & Impact</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div>
              <h3 className="text-2xl font-bold mb-6">Quantitative Results</h3>
              <div className="space-y-4">
                {caseStudy.results.quantitative.map((result, index) => (
                  <div key={index} className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold">{result.metric}</span>
                      <Badge className="bg-emerald-600 text-white">{result.improvement}</Badge>
                    </div>
                    <div className="text-sm text-slate-600">
                      <span>Before: {result.before}</span>
                      <span className="mx-2">→</span>
                      <span>After: {result.after}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold mb-6">Qualitative Benefits</h3>
              <ul className="space-y-3">
                {caseStudy.results.qualitative.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-slate-900 text-white rounded-2xl p-8 text-center">
            <div className="text-4xl mb-4">💬</div>
            <blockquote className="text-xl mb-6 italic">"{caseStudy.testimonial.quote}"</blockquote>
            <div>
              <p className="font-semibold">{caseStudy.testimonial.author}</p>
              <p className="text-slate-300">{caseStudy.testimonial.company}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Download Form Modal */}
      {showDownloadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <h3 className="text-2xl font-bold mb-2">Download Full Case Study</h3>
              <p className="text-slate-600 mb-6">Get the complete case study with detailed insights, technical specifications, and implementation guide.</p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Full Name *</label>
                  <Input
                    name="name"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Email Address *</label>
                  <Input
                    name="email"
                    type="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Company *</label>
                  <Input
                    name="company"
                    placeholder="Your Company Name"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Job Title</label>
                  <Input
                    name="jobTitle"
                    placeholder="e.g. CTO, Product Manager"
                    value={formData.jobTitle}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Phone Number</label>
                  <Input
                    name="phone"
                    placeholder="+65 9123 4567"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">What interests you most?</label>
                  <Textarea
                    name="interests"
                    placeholder="e.g. AI implementation, similar project, technology stack..."
                    value={formData.interests}
                    onChange={handleInputChange}
                    rows={3}
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    <Download className="w-4 h-4 mr-2" />
                    Download Case Study
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowDownloadForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-16 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Achieve Similar Results?</h2>
          <p className="text-xl text-slate-300 mb-8">
            Let's discuss how we can help you solve similar challenges and transform your business operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Strategy Call
            </Button>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-slate-400 text-white hover:bg-white/10">
                Explore Our Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyDetailPage;